import { Client, type Room } from "@colyseus/sdk";
import { GameState, PUBLIC_ROOM_ID, ROYALE_ROOM_ID, type CrewChoice, type GameMode, type JoinOptions, type SeaStatus } from "@tiny-seas/shared";
import { savedLookCode } from "./scene/character-look.ts";

/** Resolved game server URL: build-time VITE_SERVER_URL, else dev port 2567 or same origin. */
export function serverUrl(): string {
  const configured = import.meta.env.VITE_SERVER_URL;
  if (typeof configured === "string" && configured.length > 0) return configured;
  if (import.meta.env.DEV) return `ws://${location.hostname}:2567`;
  return location.origin.replace(/^http/, "ws");
}

export type GameRoom = Room<unknown, GameState>;

export type JoinErrorKind = "full" | "closed" | "unreachable" | "rejected";

export class JoinError extends Error {
  constructor(
    readonly kind: JoinErrorKind,
    message: string,
  ) {
    super(message);
  }
}

const RECONNECT_KEY = "tiny-seas.reconnect";

function mapError(e: unknown): JoinError {
  console.error("[tiny-seas] join failed", e);
  const message = e instanceof Error ? e.message : String(e);
  if (/locked|full/i.test(message)) return new JoinError("full", "The sea is full. Try again in a moment.");
  if (/not found|expired|disposed/i.test(message)) return new JoinError("closed", "The sea isn't open right now. Try again shortly.");
  const code = typeof e === "object" && e !== null && "code" in e ? e.code : undefined;
  if (typeof code === "number" && code >= 400 && code < 500 && message) return new JoinError("rejected", message);
  return new JoinError("unreachable", "Can't reach the server.");
}

export class Net {
  readonly client = new Client(serverUrl());

  async join(name: string, crew?: CrewChoice, mode: GameMode = "sea"): Promise<GameRoom> {
    try {
      const look = savedLookCode();
      const options: JoinOptions = { name, ...(crew === undefined ? {} : { crew }), ...(look ? { look } : {}) };
      const room = await this.client.joinById<GameState>(mode === "royale" ? ROYALE_ROOM_ID : PUBLIC_ROOM_ID, options, GameState);
      this.remember(room);
      return room;
    } catch (e) {
      throw mapError(e);
    }
  }

  /** Re-claims a seat after a page reload, within the reconnection window. */
  async resume(): Promise<GameRoom | null> {
    const token = sessionStorage.getItem(RECONNECT_KEY);
    if (!token) return null;
    try {
      const room = await this.client.reconnect<GameState>(token, GameState);
      this.remember(room);
      return room;
    } catch {
      sessionStorage.removeItem(RECONNECT_KEY);
      return null;
    }
  }

  remember(room: GameRoom): void {
    sessionStorage.setItem(RECONNECT_KEY, room.reconnectionToken);
  }

  forget(): void {
    sessionStorage.removeItem(RECONNECT_KEY);
  }
}

/** The server's HTTP `/status` endpoint, beside the WebSocket URL. */
export function statusUrl(mode: GameMode = "sea"): URL {
  const url = new URL(serverUrl().replace(/^ws/, "http"));
  url.pathname = "/status";
  url.search = mode === "royale" ? "?mode=royale" : "";
  url.hash = "";
  return url;
}

export async function fetchSeaStatus(mode: GameMode = "sea"): Promise<SeaStatus | null> {
  try {
    const response = await fetch(statusUrl(mode));
    if (!response.ok) return null;
    const value: unknown = await response.json();
    if (typeof value !== "object" || value === null) return null;
    const fields = ["humans", "reserved", "crewBots", "npcSailors", "population", "capacity"] as const;
    for (const field of fields) {
      if (!(field in value)) return null;
      const count = (value as Record<string, unknown>)[field];
      if (typeof count !== "number" || !Number.isInteger(count) || count < 0) return null;
    }
    const status = value as SeaStatus;
    if (status.capacity < 1 || status.capacity > 100 || status.population !== status.humans + status.reserved + status.crewBots + status.npcSailors || status.population > status.capacity) return null;
    if ("royale" in value && !validRoyaleStatus(status.royale)) return null;
    return status;
  } catch {
    return null;
  }
}

const ROYALE_PHASES: readonly unknown[] = ["waiting", "countdown", "active", "ended"];

function validRoyaleStatus(value: unknown): boolean {
  if (typeof value !== "object" || value === null) return false;
  const r = value as Record<string, unknown>;
  if (!ROYALE_PHASES.includes(r.phase)) return false;
  return [r.phaseIn, r.round, r.alive].every((n) => typeof n === "number" && Number.isInteger(n) && n >= 0);
}
